import { Award, Building2, HeartHandshake } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { LeadForm } from "@/components/forms/LeadForm";
import { PageHero } from "@/components/shared/PageHero";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { useSiteContent } from "@/hooks/useSiteContent";

export default function AboutPage() {
  const { siteData } = useSiteContent();

  return (
    <div className="space-y-24 pb-6" data-testid="about-page">
      <PageHero description="Learn how Monarch approaches roofing and energy projects with integrity, communication, and practical service built for California properties." eyebrow="About Monarch" imageUrl={siteData.about_page.team_image} phone={siteData.phone} primaryHref="#about-quote-form" primaryLabel="Request consultation" testIdPrefix="about-hero" title="A company story built around reliable work and long-term property value." />

      <section className="px-4 md:px-6" data-testid="about-story-section">
        <div className="page-section grid gap-8 lg:grid-cols-[0.95fr_1.05fr] lg:items-center">
          <div className="image-shell aspect-[5/4] overflow-hidden rounded-[2rem]" data-testid="about-story-image-shell">
            <img alt="Monarch team planning a project" className="h-full w-full object-cover" data-testid="about-story-image" src={siteData.about_page.team_image} />
          </div>
          <div className="space-y-6">
            <SectionHeading description={siteData.about_page.story} eyebrow="Company story" testIdPrefix="about-story-heading" title="Monarch Solar & Roofing Solutions focuses on clear communication and dependable execution." />
            <div className="grid gap-4 md:grid-cols-2">
              <Card className="rounded-[2rem] border-white/60 bg-white/90" data-testid="about-mission-card">
                <CardContent className="space-y-3 p-6">
                  <HeartHandshake className="h-8 w-8 text-primary" />
                  <h3 className="text-2xl font-bold text-foreground" data-testid="about-mission-title">
                    Mission
                  </h3>
                  <p className="text-sm text-muted-foreground" data-testid="about-mission-description">
                    {siteData.about_page.mission}
                  </p>
                </CardContent>
              </Card>
              <Card className="rounded-[2rem] border-white/60 bg-white/90" data-testid="about-vision-card">
                <CardContent className="space-y-3 p-6">
                  <Building2 className="h-8 w-8 text-primary" />
                  <h3 className="text-2xl font-bold text-foreground" data-testid="about-vision-title">
                    Vision
                  </h3>
                  <p className="text-sm text-muted-foreground" data-testid="about-vision-description">
                    {siteData.about_page.vision}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="about-stats-section">
        <div className="page-section grid gap-6 md:grid-cols-3">
          {siteData.stats.map((stat) => (
            <Card className="rounded-[2rem] border-white/60 bg-slate-950 text-white" data-testid={`about-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={stat.label}>
              <CardContent className="space-y-3 p-6">
                <Award className="h-7 w-7 text-emerald-300" />
                <p className="text-4xl font-bold">{stat.value}</p>
                <p className="text-sm text-white/70">{stat.label}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="about-testimonials-section">
        <div className="page-section space-y-10">
          <SectionHeading description="A strong about page should reinforce trust with real social proof and simple signals of credibility." eyebrow="Client trust" testIdPrefix="about-testimonials-heading" title="Built to earn repeat business and local referrals." />
          <div className="grid gap-6 lg:grid-cols-2">
            {siteData.testimonials.slice(0, 2).map((testimonial) => (
              <Card className="rounded-[2rem] border-white/60 bg-white/90" data-testid={`about-testimonial-card-${testimonial.id}`} key={testimonial.id}>
                <CardContent className="grid gap-5 p-6 md:grid-cols-[180px_1fr] md:items-center">
                  <img alt={testimonial.name} className="aspect-square w-full rounded-[1.5rem] object-cover" data-testid={`about-testimonial-image-${testimonial.id}`} src={testimonial.image_url} />
                  <div className="space-y-3">
                    <p className="text-lg font-semibold text-foreground" data-testid={`about-testimonial-quote-${testimonial.id}`}>
                      “{testimonial.quote}”
                    </p>
                    <p className="font-semibold text-foreground" data-testid={`about-testimonial-name-${testimonial.id}`}>
                      {testimonial.name}
                    </p>
                    <p className="text-sm text-muted-foreground" data-testid={`about-testimonial-location-${testimonial.id}`}>
                      {testimonial.location}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="about-quote-section" id="about-quote-form">
        <div className="page-section">
          <LeadForm buttonLabel="Request consultation" description="Use this form to start a conversation with the Monarch team about your property goals." leadType="quote" pageSource="about-quote" testIdPrefix="about-quote" title="Let’s talk about your property" />
        </div>
      </section>
    </div>
  );
}